import { assetUrl } from "@/i18n/assets";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Link } from "@/i18n/Link";
import { ArrowRight, Calendar, User } from "lucide-react";
import { PageLayout } from "@/components/site/PageLayout";
import { Seo, breadcrumbJsonLd, organizationJsonLd } from "@/components/site/Seo";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import {
  getPosts,
  getPost,
  postImage,
  getPreloadedPostContent,
  fetchPostContent,
  type BlogPostContent,
} from "@/blog/posts";
import { postPrefetchHandlers, useIdlePrefetchPosts } from "@/blog/use-prefetch";
import NotFound from "@/pages/not-found";
import { SITE_URL } from "@/routes-data";
import { useCopy } from "@/i18n/LocaleContext";
import { useLocale } from "@/i18n/LocaleContext";

const CATEGORY_LABELS = {
  en: {
    "Axles & Suspension": "Axles & Suspension",
    "Service & Repair": "Service & Repair",
    "Buyer Guides": "Buyer Guides",
    "Trailer Parts": "Trailer Parts",
  },
  es: {
    "Axles & Suspension": "Ejes y Suspensión",
    "Service & Repair": "Servicio y Reparación",
    "Buyer Guides": "Guías de Compra",
    "Trailer Parts": "Piezas para Remolque",
  },
};

const COPY = {
  en: {
    author: "Axle Inc. Service Team",
    read: (min: number) => `${min} min read`,
    tocTitle: "In this article",
    footerTitle: "Axle Inc. Service Team",
    footerDesc: "60+ years of combined trailer experience. Authorized Dexter Group distributor, Elkhart, IN. We answer the phone.",
    keepReading: "Keep reading",
    readMore: "Read",
    breadcrumbHome: "Home",
    breadcrumbBlog: "Blog",
    altSuffix: "- Axle Inc."
  },
  es: {
    author: "Equipo de Servicio de Axle Inc.",
    read: (min: number) => `${min} min de lectura`,
    tocTitle: "En este artículo",
    footerTitle: "Equipo de Servicio de Axle Inc.",
    footerDesc: "Más de 60 años de experiencia combinada en remolques. Distribuidor autorizado de Dexter Group, Elkhart, IN. Contestamos el teléfono.",
    keepReading: "Siga leyendo",
    readMore: "Leer",
    breadcrumbHome: "Inicio",
    breadcrumbBlog: "Blog",
    altSuffix: "- Axle Inc."
  },
};

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const { locale } = useLocale();
  const t = useCopy(COPY);
  const post = slug ? getPost(slug, locale) : undefined;
  // Content state is tagged with the slug+locale it belongs to so a pending
  // fetch never shows the previous article or the other language's body.
  const key = slug ? `${locale}:${slug}` : "";
  const [loaded, setLoaded] = useState<{ key: string; content: BlogPostContent | undefined }>(() => ({
    key,
    content: slug ? getPreloadedPostContent(slug, locale) : undefined,
  }));
  const content =
    loaded.key === key ? loaded.content : slug ? getPreloadedPostContent(slug, locale) : undefined;

  useEffect(() => {
    if (!slug) return;
    const k = `${locale}:${slug}`;
    const preloaded = getPreloadedPostContent(slug, locale);
    if (preloaded) {
      setLoaded({ key: k, content: preloaded });
      return;
    }
    let cancelled = false;
    fetchPostContent(slug, locale).then((c) => {
      if (!cancelled) setLoaded({ key: k, content: c });
    });
    return () => {
      cancelled = true;
    };
  }, [slug, locale]);

  const related = post ? getPosts(locale).filter((p) => p.slug !== post.slug).slice(0, 3) : [];
  useIdlePrefetchPosts(related.map((r) => r.slug), 3, locale);

  if (!post) return <NotFound />;

  const path = `/blog/${post.slug}/`;
  const trail = [
    { name: t.breadcrumbHome, path: "/" },
    { name: t.breadcrumbBlog, path: "/blog/" },
    { name: post.title, path },
  ];
  const toc = content?.toc;

  const formatDate = (iso: string) => new Intl.DateTimeFormat("es-US", { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" }).format(new Date(iso));

  return (
    <PageLayout>
      <Seo
        title={post.title}
        description={post.excerpt}
        path={path}
        type="article"
        image={postImage(post.slug, locale)}
        publishedTime={post.dateIso}
        modifiedTime={post.dateIso}
        author={t.author}
        jsonLd={[
          organizationJsonLd(),
          breadcrumbJsonLd(trail),
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.excerpt,
            datePublished: post.dateIso,
            dateModified: post.dateIso,
            author: { "@type": "Organization", name: t.author },
            image: `${SITE_URL}${postImage(post.slug, locale)}`,
            publisher: {
              "@type": "Organization",
              name: "Axle Inc.",
              logo: { "@type": "ImageObject", url: `${SITE_URL}/og-default.svg` },
            },
            mainEntityOfPage: `${SITE_URL}${path}`,
          },
        ]}
      />

      <section className="bg-secondary text-secondary-foreground">
        <div className="container mx-auto px-4 py-12 md:py-16 max-w-3xl">
          <div className="mb-6 [&_a]:text-secondary-foreground/70 [&_a:hover]:text-white [&_span]:text-white">
            <Breadcrumbs trail={trail} />
          </div>
          <p className="text-xs uppercase tracking-[0.22em] text-white font-semibold mb-4">{CATEGORY_LABELS[locale][post.category as keyof typeof CATEGORY_LABELS.en]}</p>
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight leading-[1.1]">
            {post.title}
          </h1>
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-secondary-foreground/70">
            <span className="inline-flex items-center gap-1.5"><User className="h-4 w-4" /> {t.author}</span>
            <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {locale === 'es' ? formatDate(post.dateIso) : post.date}</span>
            <span>{t.read(post.readMinutes)}</span>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 -mt-px max-w-3xl">
        <div className="aspect-[1200/630] w-full overflow-hidden rounded-md border border-border bg-secondary -translate-y-8 shadow-sm">
          <img
            src={assetUrl(postImage(post.slug, locale))}
            alt={`${post.title} ${t.altSuffix}`}
            width={1200}
            height={630}
            fetchPriority="high"
            decoding="async"
            className="h-full w-full object-cover"
          />
        </div>
      </div>

      <article className="container mx-auto px-4 py-4 md:py-8 max-w-3xl">
        {toc && toc.length > 0 && (
          <nav aria-label={t.tocTitle} className="mb-10 p-5 bg-muted/40 border border-border rounded-md">
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">{t.tocTitle}</p>
            <ol className="space-y-1.5 text-sm list-decimal list-inside marker:text-primary marker:font-semibold">
              {toc.map((t) => (
                <li key={t.id}>
                  <a href={`#${t.id}`} className="hover:text-primary transition-colors">{t.label}</a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        {content ? (
          <div
            className="prose prose-neutral prose-headings:scroll-mt-24 max-w-none prose-a:text-primary-strong prose-a:no-underline hover:prose-a:underline prose-h2:text-2xl md:prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:mt-8"
            dangerouslySetInnerHTML={{ __html: content.html }}
          />
        ) : (
          <div aria-hidden="true" className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-11/12" />
            <div className="h-4 bg-muted rounded w-10/12" />
            <div className="h-4 bg-muted rounded w-9/12" />
            <div className="h-4 bg-muted rounded w-11/12" />
            <div className="h-4 bg-muted rounded w-8/12" />
          </div>
        )}

        <div className="mt-14 pt-8 border-t border-border flex items-start gap-4">
          <div className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold shrink-0">AI</div>
          <div>
            <p className="font-semibold">{t.footerTitle}</p>
            <p className="text-sm text-muted-foreground leading-relaxed mt-1">
              {t.footerDesc}
            </p>
          </div>
        </div>
      </article>

      <section className="bg-muted/40 border-t border-border">
        <div className="container mx-auto px-4 py-14">
          <h2 className="text-xl md:text-2xl font-semibold mb-8">{t.keepReading}</h2>
          <div className="grid gap-5 md:grid-cols-3">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/blog/${r.slug}/`}
                {...postPrefetchHandlers(r.slug, locale)}
                className="group block bg-card border border-border rounded-md p-5 hover:border-primary hover:shadow-sm transition-all"
              >
                <p className="text-xs uppercase tracking-wider text-primary-strong font-semibold mb-2">{CATEGORY_LABELS[locale][r.category as keyof typeof CATEGORY_LABELS.en]}</p>
                <h3 className="font-semibold leading-snug group-hover:text-primary transition-colors">{r.title}</h3>
                <span className="mt-3 inline-flex items-center text-sm font-medium text-primary-strong">
                  {t.readMore} <ArrowRight className="ml-1 h-3.5 w-3.5" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
